/**
 * Way2Buy B2B – Produktkonfigurator v1.4.0 (Sofortkauf / Leasing / Miete)
 */
(function (W2B) {
  'use strict';

  const FALLBACK_FACTORS = {
    lease: { 12: 0.095, 24: 0.052, 36: 0.039 },
    rent: { 12: 0.11, 24: 0.065, 36: 0.049 },
  };

  const MODE_LABELS = {
    buy: 'Sofortkauf',
    lease: 'Leasing',
    rent: 'Miete',
  };

  const formatMoney = (amount) => {
    if (typeof W2B.formatMoney === 'function') return W2B.formatMoney(amount);
    const locale = document.body?.dataset.shopLocale || document.documentElement.lang || 'de-DE';
    const currency = document.body?.dataset.shopCurrency || 'EUR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  };

  const formatDisplayMoney = (amount, priceOnRequestLabel, forceOnRequest = false) => {
    if (forceOnRequest || !Number.isFinite(amount) || amount <= 0) {
      return priceOnRequestLabel || 'Auf Anfrage';
    }
    return formatMoney(amount);
  };

  const parsePrice = (value) => {
    const normalized = String(value ?? '0').replace(',', '.');
    const amount = parseFloat(normalized);
    return Number.isFinite(amount) ? amount : 0;
  };

  const state = {
    section: null,
    root: null,
    form: null,
    mode: 'buy',
    procurementData: null,
    isAdding: false,
  };

  function getQuantity() {
    const input = state.root?.querySelector('[data-b2b-qty]');
    const qty = parseInt(input?.value, 10);
    if (!Number.isFinite(qty) || qty < 1) return 1;
    return qty;
  }

  function getInventoryLimit() {
    const variant = getVariantData();
    if (!variant || !variant.inventory_management || variant.inventory_policy === 'continue') return null;
    const limit = parseInt(variant.inventory_quantity, 10);
    return Number.isFinite(limit) ? Math.max(0, limit) : null;
  }

  function normalizeQuantityInput(input) {
    if (!input) return;
    let val = parseInt(input.value, 10);
    if (!Number.isFinite(val) || val < 1) val = 1;
    const limit = getInventoryLimit();
    if (limit !== null && val > limit) {
      input.dataset.requestedQuantity = String(val);
      val = Math.max(1, limit);
    } else {
      delete input.dataset.requestedQuantity;
    }
    input.value = String(val);
  }

  function renderStockLimit() {
    const input = state.root?.querySelector('[data-b2b-qty]');
    const note = state.root?.querySelector('[data-b2b-stock-note]');
    const value = state.root?.querySelector('[data-b2b-stock-value]');
    const limit = getInventoryLimit();

    if (input) {
      if (limit === null) input.removeAttribute('max');
      else input.max = String(Math.max(0, limit));
    }
    if (note) note.hidden = limit === null;
    if (value && limit !== null) value.textContent = String(limit);
  }

  function getVariantData() {
    const variantInput = state.section?.querySelector('[data-product-variant-id]');
    if (!state.procurementData?.variants || !variantInput) return null;
    return state.procurementData.variants.find((v) => String(v.id) === String(variantInput.value));
  }

  function isModeEnabled(mode) {
    const variant = getVariantData();

    if (mode === 'buy') {
      return state.root?.dataset.buyEnabled !== 'false';
    }

    if (mode === 'lease') {
      if (state.root?.dataset.leaseEnabled !== 'true') return false;
      if (variant && variant.leasing_enabled === false) return false;
      return true;
    }

    if (mode === 'rent') {
      if (state.root?.dataset.rentEnabled !== 'true') return false;
      if (variant && variant.rent_enabled === false) return false;
      return true;
    }

    return false;
  }

  function getAvailableModes() {
    return ['buy', 'lease', 'rent'].filter(isModeEnabled);
  }

  function getDefaultMode() {
    const configuredDefault = state.root?.dataset.defaultMode || 'buy';
    if (isModeEnabled(configuredDefault)) return configuredDefault;
    return getAvailableModes()[0] || 'buy';
  }

  function ensureAllowedMode() {
    if (!isModeEnabled(state.mode)) {
      state.mode = getDefaultMode();
    }
  }

  function getBasePriceEuro() {
    const cents = parseInt(state.root?.dataset.basePrice, 10) || 0;
    return cents > 0 ? cents / 100 : 0;
  }

  function hasPrice() {
    if (state.root?.dataset.hidePrices === 'true') return false;
    return getBasePriceEuro() > 0;
  }

  function getActiveMonths() {
    if (state.mode === 'buy') return null;
    const panel = state.root?.querySelector(`[data-b2b-panel="${state.mode}"]`);
    const checked = panel?.querySelector('[data-b2b-term]:checked');
    return parseInt(checked?.value, 10) || 24;
  }

  function normalizeDecimal(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const normalized = String(value).trim().replace(',', '.');
    const amount = parseFloat(normalized);
    return Number.isFinite(amount) ? amount : 0;
  }

  function getFactor(mode, months) {
    const variant = getVariantData();
    const key = `${mode === 'rent' ? 'rent' : 'leasing'}_factor_${months}`;
    const specific = normalizeDecimal(variant?.[key]);
    if (specific > 0 && specific <= 10) return specific;

    const fromJson = normalizeDecimal(state.procurementData?.defaults?.fallback_factors?.[mode]?.[String(months)]);
    if (fromJson > 0 && fromJson <= 10) return fromJson;

    return FALLBACK_FACTORS[mode]?.[months] ?? 0;
  }

  function getUplift(mode) {
    const key = mode === 'rent' ? 'rent_uplift' : 'leasing_uplift';
    const uplift = normalizeDecimal(state.procurementData?.defaults?.[key]);
    return uplift > 0 ? uplift : 1;
  }

  function getRateFormula(mode) {
    const formula = state.procurementData?.defaults?.rate_formulas?.[mode];
    if (typeof formula === 'string' && formula.trim()) return formula.trim();
    return 'price * factor';
  }

  function tokenizeFormula(formula) {
    const tokens = [];
    let index = 0;
    const input = String(formula || '');

    while (index < input.length) {
      const char = input[index];
      if (/\s/.test(char)) {
        index += 1;
        continue;
      }
      if ('+-*/(),'.includes(char)) {
        tokens.push({ type: char, value: char });
        index += 1;
        continue;
      }
      if (/\d|\./.test(char)) {
        let value = '';
        while (index < input.length && /[\d.]/.test(input[index])) {
          value += input[index];
          index += 1;
        }
        const number = parseFloat(value);
        if (!Number.isFinite(number)) throw new Error('Invalid number');
        tokens.push({ type: 'number', value: number });
        continue;
      }
      if (/[a-zA-Z_]/.test(char)) {
        let name = '';
        while (index < input.length && /[a-zA-Z0-9_]/.test(input[index])) {
          name += input[index];
          index += 1;
        }
        tokens.push({ type: 'identifier', value: name });
        continue;
      }
      throw new Error('Invalid character');
    }

    return tokens;
  }

  function evaluateRateFormula(formula, variables) {
    const tokens = tokenizeFormula(formula);
    let pos = 0;
    const functions = {
      round: Math.round,
      ceil: Math.ceil,
      floor: Math.floor,
      abs: Math.abs,
      max: Math.max,
      min: Math.min,
    };

    const peek = () => tokens[pos];
    const consume = (type) => {
      const token = tokens[pos];
      if (!token || token.type !== type) return null;
      pos += 1;
      return token;
    };

    const parseExpression = () => {
      let value = parseTerm();
      while (peek()?.type === '+' || peek()?.type === '-') {
        const operator = consume(peek().type).type;
        const right = parseTerm();
        value = operator === '+' ? value + right : value - right;
      }
      return value;
    };

    const parseTerm = () => {
      let value = parseFactor();
      while (peek()?.type === '*' || peek()?.type === '/') {
        const operator = consume(peek().type).type;
        const right = parseFactor();
        value = operator === '*' ? value * right : value / right;
      }
      return value;
    };

    const parseArguments = () => {
      const args = [];
      if (consume(')')) return args;
      do {
        args.push(parseExpression());
      } while (consume(','));
      if (!consume(')')) throw new Error('Missing closing parenthesis');
      return args;
    };

    const parseFactor = () => {
      if (consume('+')) return parseFactor();
      if (consume('-')) return -parseFactor();

      const token = peek();
      if (!token) throw new Error('Unexpected end');

      if (consume('(')) {
        const value = parseExpression();
        if (!consume(')')) throw new Error('Missing closing parenthesis');
        return value;
      }

      if (token.type === 'number') {
        pos += 1;
        return token.value;
      }

      if (token.type === 'identifier') {
        pos += 1;
        const name = token.value;
        if (consume('(')) {
          const fn = functions[name];
          if (!fn) throw new Error('Function not allowed');
          const args = parseArguments();
          return fn(...args);
        }
        if (!Object.prototype.hasOwnProperty.call(variables, name)) throw new Error('Unknown variable');
        return variables[name];
      }

      throw new Error('Unexpected token');
    };

    const result = parseExpression();
    if (pos < tokens.length) throw new Error('Unexpected token');
    return Number.isFinite(result) ? result : 0;
  }

  function calculateRatePerUnit(mode, months, basePrice, quantity, serviceCosts) {
    const variant = getVariantData();
    const factor = getFactor(mode, months);
    const uplift = getUplift(mode);
    const setupFee = normalizeDecimal(variant?.setup_fee_net);
    const formula = getRateFormula(mode);
    const variables = {
      price: basePrice,
      netPrice: basePrice,
      factor,
      months,
      term: months,
      qty: quantity,
      quantity,
      uplift,
      setupFee,
      serviceMonthly: serviceCosts.monthly,
      serviceOneTime: serviceCosts.oneTime,
    };

    try {
      const calculated = evaluateRateFormula(formula, variables);
      if (calculated > 0) return calculated;
    } catch (error) {
      // Ungültige Formel: sicherer Fallback auf Faktorberechnung.
    }

    return basePrice * factor;
  }

  function getSelectedServices() {
    const checks = state.root?.querySelectorAll('[data-b2b-service]:checked') || [];
    return Array.from(checks).map((el) => ({
      id: el.value,
      title:
        el.dataset.serviceTitle ||
        el.closest('label')?.querySelector('.b2b-config__service-name')?.textContent?.trim() ||
        el.value,
      price: parsePrice(el.dataset.servicePrice),
      billing: el.dataset.serviceBilling || 'monthly_per_unit',
    }));
  }

  function calculateServiceCosts(quantity, services) {
    let monthly = 0;
    let oneTime = 0;
    let hasMissingPrice = false;

    services.forEach((service) => {
      if (service.price <= 0) {
        hasMissingPrice = true;
        return;
      }
      switch (service.billing) {
        case 'monthly_flat':
          monthly += service.price;
          break;
        case 'one_time_flat':
          oneTime += service.price;
          break;
        case 'one_time_per_unit':
          oneTime += service.price * quantity;
          break;
        case 'monthly_per_unit':
        default:
          monthly += service.price * quantity;
          break;
      }
    });

    return { monthly, oneTime, hasMissingPrice };
  }

  function billingLabel(billing) {
    switch (billing) {
      case 'one_time_per_unit':
        return 'einmalig pro Gerät';
      case 'one_time_flat':
        return 'einmalig pauschal';
      case 'monthly_flat':
        return 'monatlich pauschal';
      case 'monthly_per_unit':
      default:
        return 'monatlich pro Gerät';
    }
  }

  function setRowVisible(rowKey, visible) {
    state.root?.querySelectorAll(`[data-b2b-summary-row="${rowKey}"]`).forEach((row) => {
      row.hidden = !visible;
    });
  }

  function buildConfiguratorData() {
    const quantity = getQuantity();
    const basePrice = getBasePriceEuro();
    const priceOnRequest = !hasPrice();
    const priceOnRequestLabel = state.root?.dataset.priceOnRequest || 'Auf Anfrage';
    const months = getActiveMonths();
    const services = getSelectedServices();
    const serviceCosts = calculateServiceCosts(quantity, services);

    let ratePerUnit = 0;
    let rateTotal = 0;
    if (state.mode !== 'buy' && basePrice > 0 && months) {
      ratePerUnit = calculateRatePerUnit(state.mode, months, basePrice, quantity, serviceCosts);
      rateTotal = ratePerUnit * quantity;
    }

    const deviceTotal = basePrice * quantity;
    const monthlyGrand = rateTotal + serviceCosts.monthly;
    const oneTimeTotal = serviceCosts.oneTime;
    const buySubtotal = deviceTotal + oneTimeTotal;

    const rentOnRequest =
      priceOnRequest ||
      (state.mode === 'rent' && (ratePerUnit <= 0 || rateTotal <= 0 || monthlyGrand <= 0));

    return {
      quantity,
      months,
      basePrice,
      priceOnRequest,
      priceOnRequestLabel,
      rentOnRequest,
      deviceTotal,
      ratePerUnit,
      rateTotal,
      servicesMonthly: serviceCosts.monthly,
      oneTimeTotal,
      monthlyGrand,
      buySubtotal,
      services,
      serviceCostsMissing: serviceCosts.hasMissingPrice,
    };
  }

  function getState() {
    return {
      mode: state.mode,
      ...buildConfiguratorData(),
    };
  }

  function updateServicePriceLabels() {
    const priceOnRequestLabel = state.root?.dataset.priceOnRequest || 'Auf Anfrage';
    state.root?.querySelectorAll('[data-b2b-service]').forEach((input) => {
      const label = input.closest('label')?.querySelector('[data-service-price-label]');
      if (!label) return;
      const price = parsePrice(input.dataset.servicePrice);
      const priceText = price > 0 ? formatMoney(price) : priceOnRequestLabel;
      label.textContent = `${priceText} · ${billingLabel(input.dataset.serviceBilling)}`;
    });
  }

  function setHiddenProp(input, value, enabled) {
    if (!input) return;
    input.disabled = !enabled;
    if (enabled) input.value = value;
    else input.value = '';
  }

  function renderModeSwitch() {
    state.root?.querySelectorAll('[data-b2b-mode-button], [data-b2b-mode]').forEach((btn) => {
      const btnMode = btn.dataset.b2bModeButton || btn.dataset.b2bMode || 'buy';
      const enabled = isModeEnabled(btnMode);
      const active = enabled && btnMode === state.mode;
      btn.hidden = !enabled;
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', String(!enabled));
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
      btn.setAttribute('aria-pressed', String(active));
    });

    state.root?.querySelectorAll('[data-b2b-panel]').forEach((panel) => {
      const panelMode = panel.dataset.b2bPanel;
      const enabled = isModeEnabled(panelMode);
      panel.hidden = !enabled || panelMode !== state.mode;
    });

    const services = state.root?.querySelector('[data-b2b-services]');
    if (services) {
      services.classList.toggle('b2b-config__services--compact', state.mode === 'buy');
    }

    if (state.section) {
      state.section.dataset.activeProcMode = state.mode;
    }
  }

  function renderPriceBox(data) {
    const buyBox = state.root?.querySelector('[data-b2b-pricebox="buy"]');
    const monthlyBox = state.root?.querySelector('[data-b2b-pricebox="monthly"]');
    const buyMainPrice = buyBox?.querySelector('[data-b2b-main-price]');
    const buyPriceNote = buyBox?.querySelector('[data-b2b-price-note]');
    const monthlyMainPrice = monthlyBox?.querySelector('[data-b2b-main-price-monthly]');
    const monthlyPriceLabel = monthlyBox?.querySelector('[data-b2b-price-label-monthly]');
    const monthlyPriceNote = monthlyBox?.querySelector('[data-b2b-price-note-monthly]');
    const stickyValue = state.root?.querySelector('[data-b2b-sticky-value]');
    const stickyLabel = state.root?.querySelector('[data-b2b-sticky-label]');

    if (buyBox) buyBox.hidden = state.mode !== 'buy';
    if (monthlyBox) monthlyBox.hidden = state.mode === 'buy';

    if (state.mode === 'buy') {
      if (buyMainPrice) {
        buyMainPrice.textContent = data.priceOnRequest ? data.priceOnRequestLabel : formatMoney(data.basePrice);
      }
      if (buyPriceNote) {
        buyPriceNote.textContent = data.priceOnRequest ? '' : 'zzgl. gesetzlicher USt.';
        buyPriceNote.hidden = data.priceOnRequest;
      }
      if (stickyValue) {
        stickyValue.textContent = data.priceOnRequest ? data.priceOnRequestLabel : formatMoney(data.basePrice);
      }
      if (stickyLabel) stickyLabel.textContent = 'Sofortkauf netto';
    } else {
      const onRequest = state.mode === 'rent' ? data.rentOnRequest : data.priceOnRequest;
      if (monthlyPriceLabel) {
        monthlyPriceLabel.textContent =
          state.mode === 'rent' ? 'Monatliche Gesamtrate' : 'Monatliche Beispielrate';
      }
      if (monthlyMainPrice) {
        monthlyMainPrice.textContent = onRequest ? data.priceOnRequestLabel : formatMoney(data.monthlyGrand);
      }
      if (monthlyPriceNote) {
        monthlyPriceNote.textContent =
          state.mode === 'rent'
            ? 'Unverbindliche Beispielrate netto / Monat, vorbehaltlich Verfügbarkeit und Prüfung.'
            : 'Beispielrate netto / Monat, vorbehaltlich Prüfung';
      }
      if (stickyValue) {
        stickyValue.textContent = onRequest ? data.priceOnRequestLabel : formatMoney(data.monthlyGrand);
      }
      if (stickyLabel) stickyLabel.textContent = state.mode === 'rent' ? 'Monatliche Gesamtrate' : 'Monatsrate';
    }
  }

  function renderSummary(data) {
    const summary = state.root?.querySelector('[data-b2b-summary]');
    if (!summary) return;

    const set = (sel, text) => {
      const el = summary.querySelector(sel);
      if (el) el.textContent = text;
    };

    set('[data-sum-mode]', MODE_LABELS[state.mode] || data.priceOnRequestLabel);
    set('[data-sum-qty]', String(data.quantity));

    if (state.mode === 'buy') {
      setRowVisible('term', false);
      setRowVisible('monthly-per-device', false);
      setRowVisible('monthly-total', false);
      setRowVisible('monthly-grand', false);
      setRowVisible('onetime-total', false);
      setRowVisible('subtotal', true);
      setRowVisible('device', true);
      setRowVisible('onetime-services', data.oneTimeTotal > 0);
      setRowVisible('monthly-services', data.servicesMonthly > 0);

      const deviceLabel = summary.querySelector('[data-sum-device-label]');
      if (deviceLabel) deviceLabel.textContent = 'Sofortkauf netto';
      set('[data-sum-device]', formatDisplayMoney(data.basePrice, data.priceOnRequestLabel, data.priceOnRequest));
      set(
        '[data-sum-onetime-services]',
        data.oneTimeTotal > 0 ? formatMoney(data.oneTimeTotal) : data.priceOnRequestLabel
      );
      set(
        '[data-sum-monthly-services]',
        data.servicesMonthly > 0 ? formatMoney(data.servicesMonthly) : ''
      );
      set('[data-sum-subtotal]', formatDisplayMoney(data.buySubtotal, data.priceOnRequestLabel, data.priceOnRequest));
    } else if (state.mode === 'rent') {
      setRowVisible('term', true);
      setRowVisible('monthly-per-device', true);
      setRowVisible('monthly-total', true);
      setRowVisible('monthly-grand', true);
      setRowVisible('subtotal', false);
      setRowVisible('device', false);
      setRowVisible('onetime-services', data.oneTimeTotal > 0);
      setRowVisible('monthly-services', data.servicesMonthly > 0);
      setRowVisible('onetime-total', data.oneTimeTotal > 0);

      set('[data-sum-term]', data.months ? `${data.months} Monate` : data.priceOnRequestLabel);
      set(
        '[data-sum-rate-unit]',
        formatDisplayMoney(data.ratePerUnit, data.priceOnRequestLabel, data.rentOnRequest)
      );
      set(
        '[data-sum-rate-total]',
        formatDisplayMoney(data.rateTotal, data.priceOnRequestLabel, data.rentOnRequest)
      );
      set(
        '[data-sum-onetime-services]',
        data.oneTimeTotal > 0 ? formatMoney(data.oneTimeTotal) : ''
      );
      set(
        '[data-sum-monthly-services]',
        data.servicesMonthly > 0 ? formatMoney(data.servicesMonthly) : ''
      );
      set(
        '[data-sum-monthly-total]',
        formatDisplayMoney(data.monthlyGrand, data.priceOnRequestLabel, data.rentOnRequest)
      );
      if (data.oneTimeTotal > 0) {
        set('[data-sum-onetime-total]', formatMoney(data.oneTimeTotal));
      }
    } else {
      setRowVisible('term', true);
      setRowVisible('monthly-per-device', true);
      setRowVisible('monthly-total', true);
      setRowVisible('monthly-grand', true);
      setRowVisible('subtotal', false);
      setRowVisible('device', true);
      setRowVisible('onetime-services', data.oneTimeTotal > 0);
      setRowVisible('monthly-services', data.servicesMonthly > 0);
      setRowVisible('onetime-total', false);

      const deviceLabel = summary.querySelector('[data-sum-device-label]');
      if (deviceLabel) deviceLabel.textContent = 'Gerätewert netto';
      set('[data-sum-term]', data.months ? `${data.months} Monate` : data.priceOnRequestLabel);
      set('[data-sum-device]', formatDisplayMoney(data.basePrice, data.priceOnRequestLabel, data.priceOnRequest));
      set('[data-sum-rate-unit]', formatDisplayMoney(data.ratePerUnit, data.priceOnRequestLabel, data.priceOnRequest));
      set('[data-sum-rate-total]', formatDisplayMoney(data.rateTotal, data.priceOnRequestLabel, data.priceOnRequest));
      set(
        '[data-sum-onetime-services]',
        data.oneTimeTotal > 0 ? formatMoney(data.oneTimeTotal) : ''
      );
      set(
        '[data-sum-monthly-services]',
        data.servicesMonthly > 0 ? formatMoney(data.servicesMonthly) : ''
      );
      set('[data-sum-monthly-total]', formatDisplayMoney(data.monthlyGrand, data.priceOnRequestLabel, data.priceOnRequest));
    }

    const disclaimer = summary.querySelector('[data-sum-disclaimer]');
    if (disclaimer) {
      if (state.mode === 'rent') {
        disclaimer.textContent = disclaimer.dataset.rentDisclaimer || disclaimer.textContent;
        disclaimer.hidden = false;
      } else if (state.mode === 'lease') {
        disclaimer.textContent = disclaimer.dataset.leaseDisclaimer || disclaimer.textContent;
        disclaimer.hidden = false;
      } else {
        disclaimer.hidden = true;
      }
    }
  }

  function renderCTAs(data) {
    const variant = getVariantData();
    const available = variant ? variant.available : true;
    const labels = state.root?.dataset;

    state.root?.querySelectorAll('[data-b2b-cta]').forEach((btn) => {
      const ctaMode = btn.dataset.b2bCta;
      const enabled = isModeEnabled(ctaMode);
      const isActive = enabled && ctaMode === state.mode;
      btn.hidden = !enabled || !isActive;
      btn.disabled = !enabled || !available || state.isAdding;
      if (isActive) {
        btn.setAttribute('name', 'add');
      } else {
        btn.removeAttribute('name');
      }
    });

    const primary = state.root?.querySelector(`[data-b2b-cta="${state.mode}"]`);
    const secondary = state.root?.querySelector('[data-b2b-secondary]');
    const stickySubmit = state.root?.querySelector('[data-b2b-sticky-submit]');
    const hint = state.root?.querySelector('[data-b2b-hint]');

    if (primary) {
      if (state.mode === 'buy') {
        const buyButtonWithPrice = /anfrageliste/i.test(labels.buyButtonWithPrice || '')
          ? 'In den Warenkorb'
          : labels.buyButtonWithPrice || 'In den Warenkorb';
        primary.textContent = data.priceOnRequest
          ? labels.buyButtonNoPrice || 'Preis anfragen'
          : buyButtonWithPrice;
      } else if (state.mode === 'lease') {
        primary.textContent = labels.leaseButton || 'Leasingangebot anfragen';
      } else {
        primary.textContent = labels.rentButton || 'Mietangebot anfragen';
      }
    }

    if (stickySubmit) {
      stickySubmit.dataset.b2bSubmitMode = state.mode;
      if (primary) {
        stickySubmit.textContent = primary.textContent;
        stickySubmit.disabled = primary.disabled;
      }
      stickySubmit.setAttribute('name', 'add');
    }

    if (secondary) {
      if (state.mode === 'buy') {
        secondary.hidden = false;
        secondary.textContent = data.priceOnRequest
          ? labels.secondaryConsultation || 'Beratung anfordern'
          : labels.secondaryQuote || 'Individuelles Angebot anfragen';
        secondary.dataset.b2bSecondaryAction = data.priceOnRequest ? 'contact' : 'project';
      } else if (state.mode === 'rent') {
        secondary.hidden = false;
        secondary.textContent = labels.secondaryConsultation || 'Beratung anfordern';
        secondary.dataset.b2bSecondaryAction = 'contact';
      } else {
        secondary.hidden = true;
      }
    }

    if (hint) {
      hint.hidden = false;
      hint.textContent =
        state.mode === 'lease'
          ? labels.leaseHint || 'Unverbindliche Leasinganfrage mit Laufzeit und Zusatzleistungen.'
          : state.mode === 'rent'
            ? labels.rentHint || 'Unverbindliche Mietanfrage mit gewählter Laufzeit und Services.'
            : (/anfrageliste/i.test(labels.buyHint || '')
                ? 'Direkter Kauf zum Nettopreis – Produkt wird in den Warenkorb gelegt.'
                : labels.buyHint || 'Direkter Kauf zum Nettopreis – Produkt wird in den Warenkorb gelegt.');
    }
  }

  function buildLineItemProperties(data) {
    const onRequest = data.priceOnRequestLabel || 'Auf Anfrage';
    const buyType = state.root?.dataset.buyType || 'Sofortkauf';
    const leaseType = state.root?.dataset.leaseType || 'Leasing';
    const rentType = state.root?.dataset.rentType || 'Miete';
    const servicesList =
      data.services.length > 0 ? data.services.map((s) => s.title).join(', ') : '';

    const isBuyMode = state.mode === 'buy';
    const isRealBuyCheckout = isBuyMode && !data.priceOnRequest;
    const cartMode = isRealBuyCheckout ? 'checkout' : 'inquiry';
    const currentType = isBuyMode ? buyType : state.mode === 'rent' ? rentType : leaseType;

    const props = {
      '_W2B_Cart_Mode': cartMode,
      '_W2B_Is_Inquiry': cartMode === 'inquiry' ? 'Ja' : 'Nein',
      '_Beschaffungsart': currentType,
      'Beschaffungsart': currentType,
      'Kaufart': currentType,
      '_Ausgewählte Services': servicesList || 'Keine',
      'Ausgewählte Services': servicesList || 'Keine',
      'Preis auf Anfrage': data.priceOnRequest ? 'Ja' : 'Nein',
    };

    if (state.mode === 'buy') {
      props['_W2B_Cart_Mode'] = isRealBuyCheckout ? 'checkout' : 'inquiry';
      props['_W2B_Is_Inquiry'] = isRealBuyCheckout ? 'Nein' : 'Ja';
      props['_Beschaffungsart'] = buyType;
      props['Beschaffungsart'] = buyType;
      props['Kaufart'] = buyType;
      props['_Einzelpreis netto'] = data.priceOnRequest ? onRequest : formatMoney(data.basePrice);
      props['Einzelpreis netto'] = data.priceOnRequest ? onRequest : formatMoney(data.basePrice);
      if (data.oneTimeTotal > 0) {
        props['_Einmalige Services netto'] = formatMoney(data.oneTimeTotal);
        props['Einmalige Services netto'] = formatMoney(data.oneTimeTotal);
      }
      if (data.servicesMonthly > 0) {
        props['_Monatliche Services netto'] = formatMoney(data.servicesMonthly);
        props['Monatliche Services netto'] = formatMoney(data.servicesMonthly);
      }
    } else if (state.mode === 'rent') {
      props['_W2B_Cart_Mode'] = 'inquiry';
      props['_W2B_Is_Inquiry'] = 'Ja';
      props['_Menge'] = String(data.quantity);
      props['Menge'] = String(data.quantity);
      props['_Beschaffungsart'] = rentType;
      props['Beschaffungsart'] = rentType;
      props['Kaufart'] = rentType;
      props['_Laufzeit'] = data.months ? `${data.months} Monate` : onRequest;
      props['Laufzeit'] = data.months ? `${data.months} Monate` : onRequest;
      props['_Mietrate pro Gerät netto'] = data.rentOnRequest ? onRequest : formatMoney(data.ratePerUnit);
      props['Mietrate pro Gerät netto'] = data.rentOnRequest ? onRequest : formatMoney(data.ratePerUnit);
      props['_Mietrate gesamt netto'] = data.rentOnRequest ? onRequest : formatMoney(data.rateTotal);
      props['Mietrate gesamt netto'] = data.rentOnRequest ? onRequest : formatMoney(data.rateTotal);
      props['_Monatliche Gesamtrate netto'] = data.rentOnRequest ? onRequest : formatMoney(data.monthlyGrand);
      props['Monatliche Gesamtrate netto'] = data.rentOnRequest ? onRequest : formatMoney(data.monthlyGrand);
      if (data.oneTimeTotal > 0) {
        props['_Einmalige Services netto'] = formatMoney(data.oneTimeTotal);
        props['Einmalige Services netto'] = formatMoney(data.oneTimeTotal);
      } else if (data.serviceCostsMissing && data.services.some((s) => s.billing.includes('one_time'))) {
        props['_Einmalige Services netto'] = onRequest;
        props['Einmalige Services netto'] = onRequest;
      }
      if (data.servicesMonthly > 0) {
        props['_Monatliche Services netto'] = formatMoney(data.servicesMonthly);
        props['Monatliche Services netto'] = formatMoney(data.servicesMonthly);
      } else if (data.serviceCostsMissing && data.services.some((s) => s.billing.includes('monthly'))) {
        props['_Monatliche Services netto'] = onRequest;
        props['Monatliche Services netto'] = onRequest;
      }
    } else {
      props['_W2B_Cart_Mode'] = 'inquiry';
      props['_W2B_Is_Inquiry'] = 'Ja';
      props['_Menge'] = String(data.quantity);
      props['Menge'] = String(data.quantity);
      props['_Beschaffungsart'] = leaseType;
      props['Beschaffungsart'] = leaseType;
      props['Kaufart'] = leaseType;
      props['_Laufzeit'] = data.months ? `${data.months} Monate` : onRequest;
      props['Laufzeit'] = data.months ? `${data.months} Monate` : onRequest;
      props['_Mietrate pro Gerät netto'] = data.priceOnRequest ? onRequest : formatMoney(data.ratePerUnit);
      props['Mietrate pro Gerät netto'] = data.priceOnRequest ? onRequest : formatMoney(data.ratePerUnit);
      props['_Mietrate gesamt netto'] = data.priceOnRequest ? onRequest : formatMoney(data.rateTotal);
      props['Mietrate gesamt netto'] = data.priceOnRequest ? onRequest : formatMoney(data.rateTotal);
      props['_Monatliche Gesamtrate netto'] = data.priceOnRequest ? onRequest : formatMoney(data.monthlyGrand);
      props['Monatliche Gesamtrate netto'] = data.priceOnRequest ? onRequest : formatMoney(data.monthlyGrand);
      props['_Einmalige Services netto'] =
        data.oneTimeTotal > 0 ? formatMoney(data.oneTimeTotal) : onRequest;
      props['Einmalige Services netto'] = props['_Einmalige Services netto'];
      props['_Monatliche Services netto'] =
        data.servicesMonthly > 0 ? formatMoney(data.servicesMonthly) : onRequest;
      props['Monatliche Services netto'] = props['_Monatliche Services netto'];
    }

    return props;
  }

  function renderLineItemProperties(data) {
    const props = buildLineItemProperties(data);
    const onRequestLabel = data.priceOnRequestLabel || 'Auf Anfrage';

    const cartModeProp = state.root?.querySelector('[data-prop-cart-mode]');
    const inquiryProp = state.root?.querySelector('[data-prop-is-inquiry]');
    const beschaffung = state.root?.querySelector('[data-prop-beschaffungsart]');
    const kaufart = state.root?.querySelector('[data-prop-kaufart]');
    const menge = state.root?.querySelector('[data-prop-menge]');
    const einzel = state.root?.querySelector('[data-prop-einzelpreis]');
    const zwischensumme = state.root?.querySelector('[data-prop-zwischensumme]');
    const laufzeit = state.root?.querySelector('[data-prop-laufzeit]');
    const mietUnit = state.root?.querySelector('[data-prop-mietrate-unit]');
    const mietTotal = state.root?.querySelector('[data-prop-mietrate-total]');
    const monthlyGrand = state.root?.querySelector('[data-prop-monthly-grand]');
    const onetimeServices = state.root?.querySelector('[data-prop-onetime-services]');
    const monthlyServices = state.root?.querySelector('[data-prop-monthly-services]');
    const servicesProp = state.root?.querySelector('[data-prop-services]');
    const onRequestProp = state.root?.querySelector('[data-b2b-prop-on-request]');

    if (cartModeProp) cartModeProp.value = props['_W2B_Cart_Mode'] || 'checkout';
    if (inquiryProp) inquiryProp.value = props['_W2B_Is_Inquiry'] || 'Nein';
    if (beschaffung) beschaffung.value = props['_Beschaffungsart'] || '';
    if (kaufart) kaufart.value = props['Kaufart'] || props['_Beschaffungsart'] || '';
    if (menge) menge.value = props['_Menge'] || '1';
    if (servicesProp) servicesProp.value = props['_Ausgewählte Services'] || '';

    const isBuy = state.mode === 'buy';
    const isRecurring = state.mode !== 'buy';

    setHiddenProp(einzel, props['_Einzelpreis netto'] || '', isBuy);
    setHiddenProp(zwischensumme, props['_Zwischensumme netto'] || '', isBuy);
    setHiddenProp(laufzeit, props['_Laufzeit'] || '', isRecurring);
    setHiddenProp(mietUnit, props['_Mietrate pro Gerät netto'] || '', isRecurring);
    setHiddenProp(mietTotal, props['_Mietrate gesamt netto'] || '', isRecurring);
    setHiddenProp(monthlyGrand, props['_Monatliche Gesamtrate netto'] || '', isRecurring);
    setHiddenProp(
      onetimeServices,
      props['_Einmalige Services netto'] || '',
      isBuy ? data.oneTimeTotal > 0 : isRecurring
    );
    setHiddenProp(
      monthlyServices,
      props['_Monatliche Services netto'] || '',
      data.servicesMonthly > 0
    );

    if (onRequestProp) {
      onRequestProp.value = data.priceOnRequest ? 'Ja' : 'Nein';
    }
  }

  function renderServicesCount() {
    const countEl = state.root?.querySelector('[data-b2b-services-count]');
    if (!countEl) return;
    const selected = state.root?.querySelectorAll('[data-b2b-service]:checked').length || 0;
    countEl.textContent = `${selected} ausgewählt`;
  }

  function showCartFeedback(type, message, options = {}) {
    let box = state.root?.querySelector('[data-b2b-cart-feedback]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'b2b-config__cart-feedback';
      box.setAttribute('data-b2b-cart-feedback', '');
      state.root?.querySelector('.b2b-config__actions')?.after(box);
    }
    box.setAttribute('role', type === 'error' ? 'alert' : 'status');
    box.classList.toggle('b2b-config__cart-feedback--error', type === 'error');
    box.classList.toggle('b2b-config__cart-feedback--success', type === 'success');
    box.replaceChildren(document.createTextNode(message));

    if (options.contactUrl) {
      box.appendChild(document.createTextNode(' '));
      const link = document.createElement('a');
      link.href = options.contactUrl;
      link.textContent = options.contactLabel || 'Über das Kontaktformular anfragen';
      link.className = 'b2b-config__cart-feedback-link';
      box.appendChild(link);
      box.appendChild(document.createTextNode('.'));
    }

    box.hidden = false;
  }

  function getCartAddUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/add.js`;
  }

  function getCartChangeUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/change.js`;
  }

  function getCartUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`;
  }

  async function readJson(response) {
    return response.json().catch(() => ({}));
  }

  function inventoryErrorMessage(requested) {
    const limit = getInventoryLimit();
    if (limit !== null) {
      return `Derzeit sind nur ${limit} Stück verfügbar / auf Lager.`;
    }
    return `Die gewünschte Menge von ${requested} Stück ist derzeit nicht vollständig verfügbar.`;
  }

  async function addToCart() {
    if (state.isAdding) return;
    ensureAllowedMode();

    const variantInput = state.section?.querySelector('[data-product-variant-id]');
    const variantId = parseInt(variantInput?.value, 10);
    if (!variantId) return;

    const data = buildConfiguratorData();
    const qtyInput = state.root?.querySelector('[data-b2b-qty]');
    const attemptedQuantity = Math.max(1, parseInt(qtyInput?.dataset.requestedQuantity || data.quantity, 10) || 1);
    const requestedQuantity = Math.max(1, parseInt(data.quantity, 10) || 1);
    const inventoryLimit = getInventoryLimit();

    if (inventoryLimit === 0) {
      showCartFeedback('error', 'Dieser Artikel ist derzeit nicht auf Lager.');
      return;
    }

    const properties = buildLineItemProperties(data);
    state.isAdding = true;
    renderCTAs(data);

    try {
      const beforeResponse = await fetch(getCartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const beforeCart = await readJson(beforeResponse);
      if (!beforeResponse.ok) throw new Error('Der aktuelle Warenkorb konnte nicht gelesen werden.');

      const quantityBefore = (beforeCart.items || [])
        .filter((item) => Number(item.variant_id) === variantId)
        .reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);

      const availableToAdd = inventoryLimit === null ? requestedQuantity : Math.max(0, inventoryLimit - quantityBefore);
      const quantityToAdd = Math.min(requestedQuantity, availableToAdd);

      if (quantityToAdd < 1) {
        const contactUrl = state.root?.dataset.contactUrl || '/pages/contact';
        showCartFeedback('error', `Die maximal verfügbare Menge von ${inventoryLimit} Stück befindet sich bereits in Ihrem Warenkorb.`, {
          contactUrl,
          contactLabel: 'Größeren Bedarf anfragen'
        });
        state.isAdding = false;
        renderConfigurator();
        return;
      }

      const addResponse = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantityToAdd, properties }] }),
      });
      const addResult = await readJson(addResponse);
      if (!addResponse.ok) throw new Error(addResult.description || addResult.message || inventoryErrorMessage(requestedQuantity));

      const verifyResponse = await fetch(getCartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const verifiedCart = await readJson(verifyResponse);
      if (!verifyResponse.ok) throw new Error('Der aktualisierte Warenkorb konnte nicht geprüft werden.');

      const quantityAfter = (verifiedCart.items || [])
        .filter((item) => Number(item.variant_id) === variantId)
        .reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
      const actuallyAdded = Math.max(0, quantityAfter - quantityBefore);

      if (actuallyAdded < 1) throw new Error('Der Artikel konnte nicht in den Warenkorb gelegt werden.');
      if (actuallyAdded !== quantityToAdd) {
        throw new Error(`Die gewünschte Menge konnte nicht vollständig übernommen werden. Tatsächlich wurden ${actuallyAdded} Stück hinzugefügt.`);
      }

      const wasCapped = quantityToAdd < attemptedQuantity || quantityToAdd < requestedQuantity;
      if (wasCapped && inventoryLimit !== null) {
        const contactUrl = state.root?.dataset.contactUrl || '/pages/contact';
        showCartFeedback(
          'error',
          `Derzeit sind insgesamt maximal ${inventoryLimit} Stück bestellbar. Es wurden ${actuallyAdded} weitere Stück hinzugefügt; im Warenkorb befinden sich nun ${quantityAfter} Stück.`,
          { contactUrl, contactLabel: 'Größeren Bedarf anfragen' }
        );
        state.isAdding = false;
        renderConfigurator();
        return;
      }

      const redirectCheckout = state.root?.dataset.redirectCheckout === 'true';
      const cartUrl = state.root?.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
      if (redirectCheckout && state.mode === 'buy') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        return;
      }
      window.location.href = cartUrl;
    } catch (error) {
      console.error('[B2B Configurator]', error);
      showCartFeedback('error', error?.message || (state.mode === 'buy'
        ? 'Das Produkt konnte nicht in den Warenkorb gelegt werden.'
        : 'Das Produkt konnte nicht zur Anfrageliste hinzugefügt werden.'));
      state.isAdding = false;
      renderConfigurator();
    }
  }

  function renderConfigurator() {
    if (!state.root) return;
    ensureAllowedMode();
    const data = buildConfiguratorData();
    renderModeSwitch();
    renderPriceBox(data);
    renderSummary(data);
    renderCTAs(data);
    renderLineItemProperties(data);
    renderServicesCount();
    renderStockLimit();
  }

  function setMode(mode) {
    if (!['buy', 'lease', 'rent'].includes(mode)) return;
    if (!isModeEnabled(mode)) {
      state.mode = getDefaultMode();
    } else {
      state.mode = mode;
    }
    renderConfigurator();
  }

  function handleSecondaryAction() {
    const labels = state.root?.dataset;
    const action = state.root?.querySelector('[data-b2b-secondary]')?.dataset.b2bSecondaryAction;
    const url =
      action === 'contact'
        ? labels.contactUrl || '/pages/contact'
        : labels.projectUrl || '/pages/project';
    if (url) window.location.href = url;
  }

  function initStickyBar() {
    const sticky = state.root?.querySelector('[data-b2b-sticky]');
    const actions = state.root?.querySelector('.b2b-config__actions');
    if (!sticky || !actions || !('IntersectionObserver' in window) || sticky.dataset.stickyBound === 'true') {
      return;
    }
    sticky.dataset.stickyBound = 'true';

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.hidden = entry.isIntersecting;
      },
      { root: null, threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(actions);
  }

  function bindFormSubmit() {
    state.form =
      state.section?.querySelector('#product-form') ||
      state.section?.querySelector('.product-buybox__form');
    if (!state.form || state.form.dataset.b2bFormBound === 'true') return;
    state.form.dataset.b2bFormBound = 'true';

    state.form.addEventListener('submit', (event) => {
      const submitter = event.submitter;
      if (!submitter?.hasAttribute('data-b2b-submit')) return;
      event.preventDefault();

      const submitMode = submitter.dataset.b2bSubmitMode || submitter.dataset.b2bCta;
      if (['buy', 'lease', 'rent'].includes(submitMode)) {
        state.mode = submitMode;
        renderConfigurator();
      }

      addToCart();
    });
  }

  function bindEvents() {
    if (state.root?.dataset.b2bBound === 'true') return;
    state.root.dataset.b2bBound = 'true';

    state.root.addEventListener('click', (event) => {
      const modeBtn = event.target.closest('[data-b2b-mode-button], [data-b2b-mode]');
      if (modeBtn && state.root.contains(modeBtn)) {
        event.preventDefault();
        setMode(modeBtn.dataset.b2bModeButton || modeBtn.dataset.b2bMode || 'buy');
        return;
      }

      if (event.target.closest('[data-b2b-secondary]')) {
        event.preventDefault();
        handleSecondaryAction();
      }
    });

    state.root.addEventListener('keydown', (event) => {
      const modeBtn = event.target.closest('[data-b2b-mode-button], [data-b2b-mode]');
      if (!modeBtn || !state.root.contains(modeBtn)) return;
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.preventDefault();
      const modeButtons = Array.from(
        state.root.querySelectorAll('[data-b2b-mode-button], [data-b2b-mode]')
      ).filter((btn) => !btn.hidden && !btn.disabled);
      if (!modeButtons.length) return;
      const index = modeButtons.indexOf(modeBtn);
      const nextIndex =
        event.key === 'ArrowRight'
          ? (index + 1) % modeButtons.length
          : (index - 1 + modeButtons.length) % modeButtons.length;
      modeButtons[nextIndex].focus();
      setMode(modeButtons[nextIndex].dataset.b2bModeButton || modeButtons[nextIndex].dataset.b2bMode || 'buy');
    });

    state.root.addEventListener('change', (event) => {
      if (event.target.matches('[data-b2b-term], [data-b2b-service], [data-b2b-qty]')) {
        if (event.target.matches('[data-b2b-qty]')) {
          normalizeQuantityInput(event.target);
        }
        renderConfigurator();
      }
    });

    state.root.addEventListener('input', (event) => {
      if (event.target.matches('[data-b2b-qty]')) {
        renderConfigurator();
      }
    });

    state.root.addEventListener('blur', (event) => {
      if (event.target.matches('[data-b2b-qty]')) {
        normalizeQuantityInput(event.target);
        renderConfigurator();
      }
    }, true);
  }

  function loadProcurementData() {
    const variantsJsonEl = state.section?.querySelector('[data-procurement-variants-json]');
    try {
      state.procurementData = variantsJsonEl ? JSON.parse(variantsJsonEl.textContent) : null;
    } catch {
      state.procurementData = null;
    }
  }

  W2B.initB2BConfigurator = function initB2BConfigurator() {
    state.section = document.querySelector('[data-product-section]');
    state.root = state.section?.querySelector('[data-b2b-configurator]');
    if (!state.root) return;

    loadProcurementData();
    updateServicePriceLabels();
    bindEvents();
    bindFormSubmit();
    initStickyBar();

    if (state.root.dataset.b2bInitialized !== 'true') {
      state.root.dataset.b2bInitialized = 'true';
      state.mode = getDefaultMode();
      const qtyInput = state.root.querySelector('[data-b2b-qty]');
      if (qtyInput) {
        qtyInput.value = '1';
        qtyInput.min = '1';
      }
    }

    renderConfigurator();
  };

  W2B.updateB2BConfigurator = function updateB2BConfigurator() {
    const section = document.querySelector('[data-product-section]');
    const root = section?.querySelector('[data-b2b-configurator]');
    if (!root) return;

    state.section = section;
    state.root = root;
    loadProcurementData();

    const variantInput = section.querySelector('[data-product-variant-id]');
    const variant = state.procurementData?.variants?.find((v) => String(v.id) === String(variantInput?.value));
    if (variant) {
      root.dataset.basePrice = String(variant.price || 0);
    }

    ensureAllowedMode();
    renderConfigurator();
  };

  W2B.getB2BConfiguratorState = getState;
})(window.W2B = window.W2B || {});
